import axios from "axios";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
export const TOKEN_KEY = "calendery_admin_token";

export const publicApi = axios.create({ baseURL: BASE });
export const adminApi = axios.create({ baseURL: BASE });

// Attach the bearer token to every admin request.
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On an expired/invalid session, drop the token and bounce to the login page.
adminApi.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.endsWith("/login")) {
      localStorage.removeItem(TOKEN_KEY);
      if (!window.location.pathname.endsWith("/admin/login")) {
        window.location.assign("/admin/login");
      }
    }
    return Promise.reject(error);
  }
);

/* ---------------- Public booking API ---------------- */
export const PublicBookingApi = {
  getBooking: (token) => publicApi.get(`/api/booking/${token}`).then((r) => r.data),
  createBooking: (token, payload) => publicApi.post(`/api/booking/${token}`, payload).then((r) => r.data),
  getResult: (resultToken) => publicApi.get(`/api/booking/result/${resultToken}`).then((r) => r.data),
  cancelInfo: (cancelToken) => publicApi.get(`/api/booking/cancel/${cancelToken}`).then((r) => r.data),
  cancel: (cancelToken) => publicApi.delete(`/api/booking/cancel/${cancelToken}`).then((r) => r.data),
  // Name-based "manage my booking" lookup (returns matching active bookings + manage tokens).
  findBookings: (token, lastName) => publicApi.post(`/api/booking/${token}/find`, { lastName }).then((r) => r.data),
  reschedule: (manageToken, payload) => publicApi.post(`/api/booking/manage/${manageToken}/reschedule`, payload).then((r) => r.data),
};

/* ---------------- Admin API ---------------- */
export const AdminApi = {
  login: (email, password) => adminApi.post(`/api/admin/login`, { email, password }).then((r) => r.data),
  me: () => adminApi.get(`/api/admin/me`).then((r) => r.data),
  stats: () => adminApi.get(`/api/admin/stats`).then((r) => r.data),

  listSlots: (from, to) =>
    adminApi.get(`/api/admin/slots`, { params: { from, to } }).then((r) => r.data.slots),
  createSlot: (payload) => adminApi.post(`/api/admin/slots`, payload).then((r) => r.data),
  createSeries: (payload) => adminApi.post(`/api/admin/slots/series`, payload).then((r) => r.data),
  deleteSlot: (id) => adminApi.delete(`/api/admin/slots/${id}`).then((r) => r.data),
  bulkDeleteSlots: (payload) => adminApi.post(`/api/admin/slots/bulk-delete`, payload).then((r) => r.data),
  bulkDurationSlots: (payload) => adminApi.post(`/api/admin/slots/bulk-duration`, payload).then((r) => r.data),

  listBlackouts: () => adminApi.get(`/api/admin/blackouts`).then((r) => r.data.blackouts),
  createBlackout: (payload) => adminApi.post(`/api/admin/blackouts`, payload).then((r) => r.data),
  deleteBlackout: (id) => adminApi.delete(`/api/admin/blackouts/${id}`).then((r) => r.data),

  listRules: () => adminApi.get(`/api/admin/availability-rules`).then((r) => r.data.rules),
  createRule: (payload) => adminApi.post(`/api/admin/availability-rules`, payload).then((r) => r.data),
  deleteRule: (id) => adminApi.delete(`/api/admin/availability-rules/${id}`).then((r) => r.data),
  rollSlots: (weeks) => adminApi.post(`/api/admin/slots/roll`, { weeks }).then((r) => r.data),
  slotDurations: () => adminApi.get(`/api/admin/slots/durations`).then((r) => r.data.durations),

  listBookings: (params) => adminApi.get(`/api/admin/bookings`, { params }).then((r) => r.data),
  cancelBooking: (id) => adminApi.post(`/api/admin/bookings/${id}/cancel`).then((r) => r.data),

  listLinks: () => adminApi.get(`/api/admin/links`).then((r) => r.data.links),
  createLink: (payload) => adminApi.post(`/api/admin/links`, payload).then((r) => r.data),
  updateLink: (id, payload) => adminApi.patch(`/api/admin/links/${id}`, payload).then((r) => r.data),
  deleteLink: (id) => adminApi.delete(`/api/admin/links/${id}`).then((r) => r.data),

  async downloadBookingsCsv(params) {
    const res = await adminApi.get(`/api/admin/bookings/export.csv`, { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `buchungen_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};
